import React from "react";
import { Link, redirect, useSubmit } from "react-router-dom";
import { useFormik } from 'formik';
import { Button } from 'primereact/button';
import { InputText } from "primereact/inputtext";
import { Password } from 'primereact/password';
import { Divider } from 'primereact/divider';
import { classNames } from 'primereact/utils';
import AuthLayout, { AuthField } from "../../modules/AuthLayout";
import { register } from "../../../data/accounts";
import { isConnected } from "../../../data/auth.service";
import { send, notify } from "../../../services/NotificationService";

export async function action({ request }) {
    const formData = await request.formData();
    const data = Object.fromEntries(formData);

    const rt = await register(data);

    if (rt && rt.status === 400) {
        send("warning", rt.message);
        notify();
        return null;
    }

    return redirect(`/`);
}

export async function loader() {
    if (isConnected()) {
        return redirect("/");
    }

    return {};
}

export default function Register() {
    const submit = useSubmit();

    const passwordHeader = <div className="font-bold mb-3">Choisir un mot de passe</div>;
    const passwordFooter = (
        <>
            <Divider />
            <ul data-cy="infoPwd">
                <li>Au moins une minuscule</li>
                <li>Au moins une majuscule</li>
                <li>Au moins un chiffre</li>
                <li>Au moins un caractère non alphanumérique</li>
                <li>Minimum 6 caractères</li>
            </ul>
        </>
    );

    const formik = useFormik({
        initialValues: {
            registerFname: '',
            registerLname: '',
            registerEmail: '',
            registerPwd: '',
            registerPwdConfirm: ''
        },
        validate: (data) => {
            const errors = {};
            if (!data.registerFname) {
                errors.registerFname = "Le prénom est obligatoire.";
            }
            if (!data.registerLname) {
                errors.registerLname = "Le nom est obligatoire.";
            }
            if (!data.registerEmail) {
                errors.registerEmail = "L'adresse e-mail est obligatoire.";
            }
            if (!data.registerPwd) {
                errors.registerPwd = "Le mot de passe est obligatoire.";
            }
            if (data.registerPwd && data.registerPwdConfirm !== data.registerPwd) {
                errors.registerPwdConfirm = "Les mots de passe ne correspondent pas.";
            }
            return errors;
        },
        onSubmit: async (data) => {
            const { registerPwdConfirm, ...payload } = data;
            submit(payload, { method: "post" });
        }
    });

    const isFormFieldInvalid = (name) => !!(formik.touched[name] && formik.errors[name]);

    return (
        <AuthLayout
            title="Créer mon espace loueur"
            subtitle="Quelques informations suffisent pour commencer à gérer vos locations."
            footer={<>Vous avez déjà un compte ? <Link to="/login">Se connecter</Link></>}
        >
            <form onSubmit={formik.handleSubmit} noValidate>
                <div className="lk-field-row">
                    <AuthField id="registerFname" label="Prénom" error={isFormFieldInvalid('registerFname') ? formik.errors.registerFname : null}>
                        <InputText
                            id="registerFname"
                            name="registerFname"
                            autoComplete="given-name"
                            value={formik.values.registerFname}
                            data-cy="fnameInput"
                            onChange={(e) => formik.setFieldValue('registerFname', e.target.value)}
                            className={classNames({ 'p-invalid': isFormFieldInvalid('registerFname') })}
                        />
                    </AuthField>
                    <AuthField id="registerLname" label="Nom" error={isFormFieldInvalid('registerLname') ? formik.errors.registerLname : null}>
                        <InputText
                            id="registerLname"
                            name="registerLname"
                            autoComplete="family-name"
                            value={formik.values.registerLname}
                            data-cy="lnameInput"
                            onChange={(e) => formik.setFieldValue('registerLname', e.target.value)}
                            className={classNames({ 'p-invalid': isFormFieldInvalid('registerLname') })}
                        />
                    </AuthField>
                </div>

                <AuthField id="registerEmail" label="Adresse e-mail" error={isFormFieldInvalid('registerEmail') ? formik.errors.registerEmail : null}>
                    <InputText
                        id="registerEmail"
                        name="registerEmail"
                        type="email"
                        autoComplete="email"
                        inputMode="email"
                        value={formik.values.registerEmail}
                        data-cy="emailInput"
                        onChange={(e) => formik.setFieldValue('registerEmail', e.target.value)}
                        className={classNames({ 'p-invalid': isFormFieldInvalid('registerEmail') })}
                    />
                </AuthField>

                <AuthField id="registerPwd" label="Mot de passe" error={isFormFieldInvalid('registerPwd') ? formik.errors.registerPwd : null}>
                    <Password
                        inputId="registerPwd"
                        name="registerPwd"
                        autoComplete="new-password"
                        value={formik.values.registerPwd}
                        toggleMask
                        header={passwordHeader}
                        footer={passwordFooter}
                        promptLabel=" "
                        weakLabel="Trop simple"
                        mediumLabel="Moyennement complexe"
                        strongLabel="Très complexe"
                        data-cy="pwdInput"
                        onChange={(e) => formik.setFieldValue('registerPwd', e.target.value)}
                        className={classNames({ 'p-invalid': isFormFieldInvalid('registerPwd') })}
                    />
                </AuthField>

                <AuthField id="registerPwdConfirm" label="Confirmation du mot de passe" error={isFormFieldInvalid('registerPwdConfirm') ? formik.errors.registerPwdConfirm : null}>
                    <Password
                        inputId="registerPwdConfirm"
                        name="registerPwdConfirm"
                        autoComplete="new-password"
                        value={formik.values.registerPwdConfirm}
                        feedback={false}
                        toggleMask
                        data-cy="pwdconfirmInput"
                        onChange={(e) => formik.setFieldValue('registerPwdConfirm', e.target.value)}
                        className={classNames({ 'p-invalid': isFormFieldInvalid('registerPwdConfirm') })}
                    />
                </AuthField>

                <div className="lk-auth__actions">
                    <Button type="submit" label="Créer mon compte" icon="pi pi-user-plus" data-cy="registerBtn" />
                </div>
            </form>
        </AuthLayout>
    );
}
